/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import { Grid } from '@/components/Grid';
import { useRouter } from 'next/router';
import { SearchResultProduct } from '../SearchResultProduct';
import PaginationComp from '@/components/AZCustomComponent/Pagination/Pagination';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import styles from './styles.module.scss';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { SearchNoResults } from './SearchNoResults';
import type { SearchPageBodyRecordsElement } from '../../interface';

type Props = {
  searchResult: SearchPageBodyRecordsElement | undefined;
  queryDisplayLabel?: string;
  viewingPartTypes?: boolean;
};

export const SearchResults = ({
  searchResult,
  queryDisplayLabel,
  viewingPartTypes = false,
}: Props) => {
  const { data: searchPartTypesData } = useSearchPartTypes({
    enabled: viewingPartTypes,
    viewingPartTypes,
  });

  const router = useRouter();

  const fetchPaginatedList = (currentPage: number) => {
    const { query, asPath } = router;
    const newQuery: { currentPage: number; partTypesView?: string } = { ...query, currentPage };
    if (viewingPartTypes) {
      newQuery.partTypesView = 'true';
    }
    void router.push({
      pathname: getPathnameFromAsPath(asPath),
      query: newQuery,
    });
  };

  const {
    pagination = {
      total: searchPartTypesData?.total ?? 0,
      firstRecordNumberOfCurrentPage: searchPartTypesData?.firstRecNum ?? 0,
      pageSize: searchPartTypesData?.recordsPerPage ?? 0,
    },
    records = searchPartTypesData?.records ?? [],
    totalNumRecs = searchPartTypesData?.total ?? 0,
    recsPerPage = searchPartTypesData?.recordsPerPage ?? 0,
  } = searchResult ?? {};

  const isPaginationEnabled = totalNumRecs > recsPerPage;

  if (totalNumRecs === 0) {
    return <SearchNoResults queryDisplayLabel={queryDisplayLabel} />;
  }

  return (
    <>
      <div
        id="search-result-list"
        data-testid="search-result-list"
        className={styles.flexContainer}
      >
        {records.length > 0 &&
          records.map((item, index) => (
            <Grid
              item
              xs={4}
              className={styles.imageContainer}
              key={`${item.propertyMap.P_ItemId[0]}${index}`}
              component="article"
              role="article"
            >
              <SearchResultProduct
                searchText={
                  viewingPartTypes ? (router.query.searchText as string | undefined) : undefined
                }
                content={item}
                data-testid={`product-link-${index}`}
              />
            </Grid>
          ))}

        {isPaginationEnabled && (
          <PaginationComp
            offset={pagination.firstRecordNumberOfCurrentPage}
            limit={pagination.pageSize}
            total={pagination.total}
            performFetch={(_, newPage) => fetchPaginatedList(newPage)}
            pageName="SEARCH_RESULTS"
          />
        )}
      </div>
      <div id="qualtrics-search" />
    </>
  );
};
